import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES, ROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'
import type { User } from '../models/user.model'
import type { PermissionAction, ResolvedPermissions } from '../models/permission.model'

/**
 * Auth context — the single cookie-only session model for every user
 * (operators AND customers). The staff/customer split was removed
 * 2026-05-23: identity is just a `User` + `user_type`, and what a user
 * can see/do is decided by the permission layer, not by which context
 * or cookie they hold.
 *
 * The gateway issues `uym_session` as a Secure HttpOnly cookie on
 * register / login. The cookie carries the session id; the full record
 * lives in SESSION_KV. JS never sees the session id.
 *
 * On mount: a single GET /api/account/me with credentials:'include'
 * hydrates the user (works for any active account). 200 → logged in.
 * 401 → not logged in (silent — no redirect).
 *
 * Login (`login`) calls POST /api/auth/login and returns
 * `{ user, landingPath }`; the hook hydrates the user locally and the
 * page navigates to `landingPath`. Register, email-verify-request, and
 * email-verify-confirm are the three pre-auth fetches the RegisterPage
 * drives.
 *
 * authedFetch redirects to ROUTES.LOGIN on a 401 caught mid-session.
 */
interface AuthContextValue {
  readonly user: User | null
  readonly isAuthenticated: boolean
  readonly isLoading: boolean
  readonly error: string | null
  /**
   * Resolves to the login result on success (the hook has already
   * hydrated the user locally; the page just navigates to
   * `landingPath`). Rejects with the server error message on failure.
   */
  readonly login: (email: string, password: string) => Promise<LoginResult>
  /**
   * Resolves to the server-supplied landing path on success
   * (per Rule #23 — resolved from user_types.landing_path WHERE
   * code='customer'). Rejects with the server error message on
   * failure. RegisterPage navigates the user to the returned path.
   */
  readonly register: (input: RegisterFormInput) => Promise<string>
  /**
   * Pre-auth — request a 6-digit verification code for the given email
   * (sign-up flow, stage 1 → 2). Returns the server-supplied
   * `expiresAt` so the page can run its countdown. Rejects with the
   * server error message on failure.
   */
  /** Email-verification request — the second arg is required from the
   *  Create-Account flow (server runs URL+reachability checks), absent
   *  from the email-change flow on Profile (no URL context there). */
  readonly requestEmailVerification: (
    email: string,
    organisationUrl?: string
  ) => Promise<{ expiresAt: string }>
  /**
   * Pre-auth — confirm the 6-digit code for the given email (sign-up
   * flow, stage 2 → 3). Resolves on success; rejects with the server
   * error message on a bad / expired code.
   */
  readonly confirmEmailVerification: (email: string, code: string) => Promise<void>
  readonly logout: () => Promise<void>
  readonly authedFetch: (path: string, init?: RequestInit) => Promise<Response>
  readonly setUserLocal: (next: User) => void
  /**
   * Layer-2c gate for the UI. Returns true if the signed-in user may
   * perform `action` on `screen` (a screens.code, use SCREENCODES).
   * Mirrors the server-side hasScreenPermission exactly — every
   * user_type (including admin) flows through its group grants, so
   * gated controls match what the API will actually allow.
   */
  readonly can: (screen: string, action: PermissionAction) => boolean
}

/**
 * Login result. Mirrors the gateway wire shape — one generic identity,
 * no `kind` discriminator.
 */
export interface LoginResult {
  readonly user: User
  readonly landingPath: string
}

/** Shape of the auth bootstrap payload (/account/me, login, register). */
interface AuthPayload {
  readonly user: User
  readonly permissions: ResolvedPermissions
}

export interface RegisterFormInput {
  readonly email: string
  readonly password: string
  readonly firstName: string | null
  readonly lastName: string | null
  readonly acceptsMarketing: boolean
  /** Required on master apex (the visitor is creating a new
   *  organisation, the URL anchors company identity + the email-domain
   *  match rule); omitted on tenant subdomains where the visitor is
   *  signing up to shop on the existing tenant store. Kiran 2026-06-08. */
  readonly organisationUrl?: string
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface ApiEnvelopeOk<T> {
  readonly success: true
  readonly data: T
}
interface ApiEnvelopeErr {
  readonly success: false
  readonly error: { readonly code: string; readonly message: string }
}
type ApiEnvelope<T> = ApiEnvelopeOk<T> | ApiEnvelopeErr

/**
 * Cross-tab message envelope on the `uym-tab-lock` BroadcastChannel. Keeps
 * "only one tab kept alive per user" — when a tab broadcasts `active` with a
 * userId matching the receiving tab, the receiver clears its auth state and
 * surfaces the SupplantedOverlay. `logout` from any tab clears the others too.
 */
type TabLockMessage =
  | { readonly kind: 'active'; readonly tabId: string; readonly userId: string }
  | { readonly kind: 'logout' }

const TAB_LOCK_CHANNEL = 'uym-tab-lock'

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null)
  const [permissions, setPermissions] = useState<ResolvedPermissions>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  // True when ANOTHER tab in the same browser has just signed in for the same
  // user — we tear down auth state here and show an overlay. Cleared on close
  // or on a fresh login here.
  const [supplanted, setSupplanted] = useState<boolean>(false)

  // Tab-local identity; sessionStorage would scope per-tab but `useRef` is
  // adequate (a tab never sees another tab's ref).
  const tabIdRef = useRef<string | null>(null)
  if (tabIdRef.current === null) {
    tabIdRef.current = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
  const channelRef = useRef<BroadcastChannel | null>(null)
  // Latest user is read by the channel listener via this ref so we don't
  // have to re-bind the channel each time `user` changes.
  const userRef = useRef<User | null>(null)
  userRef.current = user

  const hydrate = useCallback((payload: AuthPayload): void => {
    setUser(payload.user)
    setPermissions(payload.permissions ?? {})
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // 1. Ensure a server-side session id exists (mints an anonymous
        //    record on the first visit so the cookie is set before any
        //    state-changing request — e.g. cart actions later).
        await fetch(`${APIBASE}${APIROUTES.SESSIONINIT}`, {
          method: 'POST',
          credentials: 'include'
        }).catch(() => {
          // Network blip on boot: don't block; /account/me below will
          // either succeed (cookie was already valid) or 401 silently.
        })
        // 2. Hydrate the signed-in user, if there is one. 401 is the
        //    expected response for an anonymous session — stay logged-out.
        const res = await fetch(`${APIBASE}${APIROUTES.ACCOUNTME}`, {
          credentials: 'include'
        })
        if (!cancelled && res.ok) {
          const envelope = (await res.json()) as ApiEnvelope<AuthPayload>
          if (envelope.success) hydrate(envelope.data)
        }
      } catch {
        // Stay logged-out on boot probe failures.
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [hydrate])

  // Tab-lock channel — keeps only one logged-in tab alive per browser.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined
    const ch = new BroadcastChannel(TAB_LOCK_CHANNEL)
    channelRef.current = ch
    ch.onmessage = (e: MessageEvent<TabLockMessage>): void => {
      const my = userRef.current
      const msg = e.data
      if (msg.kind === 'active' && msg.tabId !== tabIdRef.current) {
        // Another tab claimed the active slot for THIS user — tear down here.
        if (my !== null && msg.userId === my.id) {
          setSupplanted(true)
          setUser(null)
          setPermissions({})
        }
      } else if (msg.kind === 'logout') {
        // A peer tab signed out — mirror the state locally.
        setSupplanted(false)
        setUser(null)
        setPermissions({})
      }
    }
    return () => {
      ch.close()
      channelRef.current = null
    }
  }, [])

  // Broadcast a fresh `active` claim whenever this tab becomes (or re-becomes)
  // authenticated — on login, register, and on first-mount hydration via
  // /account/me. Other tabs of the same user receive it and clear their state.
  // Also clears the SupplantedOverlay if it was up (the user is reclaiming
  // this tab by signing in here).
  useEffect(() => {
    if (user === null) return
    setSupplanted(false)
    const ch = channelRef.current
    if (ch === null) return
    const msg: TabLockMessage = { kind: 'active', tabId: tabIdRef.current as string, userId: user.id }
    ch.postMessage(msg)
  }, [user])

  /**
   * Run a fetch + JSON-parse pair that's been hand-rolled here (because
   * the auth flows are pre-session, so they bypass authedFetch/mutateJson).
   * Centralises the "network throw → SERVER_UNAVAILABLE" + "5xx →
   * SERVER_UNAVAILABLE" + "non-JSON body → SERVER_UNAVAILABLE" logic
   * so all four auth endpoints get identical treatment.
   *
   * On 4xx with our envelope, relays the server's domain message (e.g.
   * "Invalid email or password" → user sees the real reason).
   */
  const authFetchEnvelope = useCallback(
    async <T,>(url: string, init: RequestInit): Promise<T> => {
      let res: Response
      try {
        res = await fetch(url, init)
      } catch (err) {
        if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        throw err
      }
      if (res.status >= 500) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
      let envelope: ApiEnvelope<T>
      try {
        envelope = (await res.json()) as ApiEnvelope<T>
      } catch {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE)
      }
      if (!envelope.success) {
        const msg = envelope.error?.message
        throw new Error(typeof msg === 'string' && msg.length > 0 ? msg : SERVER_UNAVAILABLE_MESSAGE)
      }
      return envelope.data
    },
    []
  )

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setError(null)
    try {
      const data = await authFetchEnvelope<AuthPayload & { landingPath: string }>(
        `${APIBASE}${APIROUTES.AUTHLOGIN}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, password })
        }
      )
      hydrate(data)
      return { user: data.user, landingPath: data.landingPath }
    } catch (err) {
      const msg = err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE
      setError(msg)
      throw err instanceof Error ? err : new Error(msg)
    }
  }, [authFetchEnvelope, hydrate])

  const register = useCallback(async (input: RegisterFormInput): Promise<string> => {
    setError(null)
    try {
      const data = await authFetchEnvelope<AuthPayload & { landingPath: string }>(
        `${APIBASE}${APIROUTES.AUTHREGISTER}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(input)
        }
      )
      hydrate(data)
      return data.landingPath
    } catch (err) {
      const msg = err instanceof Error ? err.message : SERVER_UNAVAILABLE_MESSAGE
      setError(msg)
      throw err
    }
  }, [authFetchEnvelope, hydrate])

  const requestEmailVerification = useCallback(
    async (email: string, organisationUrl?: string): Promise<{ expiresAt: string }> => {
      const body = organisationUrl !== undefined && organisationUrl !== ''
        ? { email, organisationUrl }
        : { email }
      return authFetchEnvelope<{ expiresAt: string }>(
        `${APIBASE}${APIROUTES.AUTHEMAILVERIFYREQUEST}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
    },
    [authFetchEnvelope]
  )

  const confirmEmailVerification = useCallback(
    async (email: string, code: string): Promise<void> => {
      await authFetchEnvelope<{ verified: true }>(
        `${APIBASE}${APIROUTES.AUTHEMAILVERIFYCONFIRM}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, code })
        }
      )
    },
    [authFetchEnvelope]
  )

  const logout = useCallback(async () => {
    try {
      await fetch(`${APIBASE}${APIROUTES.AUTHLOGOUT}`, {
        method: 'POST',
        credentials: 'include'
      })
      // Mint a fresh anonymous session right away so the visitor keeps a
      // server-side id (for cart preservation across visits, etc.) without
      // waiting for the next app mount.
      await fetch(`${APIBASE}${APIROUTES.SESSIONINIT}`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {
        // Non-blocking; next mount will re-init if this slips.
      })
    } finally {
      // Broadcast to peer tabs so they clear their auth state too.
      if (channelRef.current !== null) {
        const msg: TabLockMessage = { kind: 'logout' }
        channelRef.current.postMessage(msg)
      }
      setUser(null)
      setPermissions({})
      setError(null)
      setSupplanted(false)
    }
  }, [])

  const authedFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      // Network-level throws (gateway unreachable, DNS, CORS, offline)
      // bubble up as TypeError and end up in `setError(err.message)`
      // call sites all over the SPA. Catch them here once and rethrow
      // with the generic SERVER_UNAVAILABLE_MESSAGE so no viewmodel
      // leaks `TypeError: Failed to fetch` or any URL/status detail.
      // AbortError (e.g. usePaginatedList cleanup) is preserved
      // unchanged because callers branch on it separately.
      let res: Response
      try {
        res = await fetch(`${APIBASE}${path}`, {
          ...init,
          credentials: 'include'
        })
      } catch (err) {
        if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        throw err
      }
      if (res.status === 401 && user !== null) {
        setUser(null)
        if (typeof window !== 'undefined' && window.location.pathname !== ROUTES.LOGIN) {
          window.location.assign(ROUTES.LOGIN)
        }
      }
      return res
    },
    [user]
  )

  const can = useCallback(
    (screen: string, action: PermissionAction): boolean =>
      permissions[screen]?.[`is_${action}`] === 1,
    [permissions]
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      error,
      login,
      register,
      requestEmailVerification,
      confirmEmailVerification,
      logout,
      authedFetch,
      setUserLocal: setUser,
      can
    }),
    [
      user,
      isLoading,
      error,
      login,
      register,
      requestEmailVerification,
      confirmEmailVerification,
      logout,
      authedFetch,
      can
    ]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      {supplanted ? <SupplantedOverlay onClose={() => setSupplanted(false)} /> : null}
    </AuthContext.Provider>
  )
}

/**
 * Overlay shown when another tab has claimed the active slot for the same
 * user. The current tab is already locally signed out (auth state cleared
 * above); the overlay just makes the reason explicit. Two actions: sign in
 * again here (which will reclaim the slot and supplant the other tab) or
 * close (dismiss; user sees the signed-out UI of the underlying page).
 */
function SupplantedOverlay({ onClose }: { readonly onClose: () => void }): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border/40 bg-bg-primary p-8 shadow-xl">
        <h2 className="font-heading text-h2 text-charcoal">Signed in from another tab</h2>
        <p className="mt-3 font-body text-body text-charcoal/90">
          You signed in to your account in another tab or window. For your security,
          this tab has been signed out.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <a
            href={ROUTES.LOGIN}
            className="flex-1 rounded-full bg-forest px-6 py-3 text-center font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
          >
            Sign in here
          </a>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-border px-6 py-3 font-body text-body text-charcoal transition hover:border-forest hover:text-forest"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
